import { useEffect, useRef, useState } from 'react';

/* ICIB-174 — dropdown that lets the Add-enrolment forms (ICIB-02a
   IntakeView, EMP-03 NewStarterView) pick one or more products for the
   same person. A native <select multiple> needs ctrl/cmd-click and shows
   as a list box rather than a dropdown, so this is a button styled like
   the other .field selects that opens a checkbox list instead. `value` is
   an array of product ids. */
function ProductMultiSelect({ products, value, onChange, disabled, placeholder = 'Select product(s)…' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on any click outside, or Escape — same as a native dropdown.
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function toggle(id) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  const names = products.filter((p) => value.includes(p.id)).map((p) => p.name);
  const label = names.length ? names.join(', ') : placeholder;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%', textAlign: 'left', border: '1px solid var(--rule)', borderRadius: 5,
          padding: '7px 9px', background: 'var(--card)', display: 'flex', alignItems: 'center', gap: 8,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, font: 'inherit', color: 'inherit',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: names.length ? undefined : 'var(--muted)' }}>{label}</span>
        <span aria-hidden="true" style={{ fontSize: 10, color: 'var(--muted)' }}>▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: 240, overflowY: 'auto',
            background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 5, boxShadow: '0 4px 14px rgba(0,0,0,.08)', padding: 4,
          }}
        >
          {products.map((p) => (
            <label
              key={p.id}
              role="option"
              aria-selected={value.includes(p.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', margin: 0, fontWeight: 400, fontSize: 13, cursor: 'pointer' }}
            >
              <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} style={{ width: 'auto' }} />
              {p.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductMultiSelect;
